import type { FC, ReactNode } from 'react'

import { useRef } from 'react'

import { cn } from '@/lib/utils'
import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'
import { Reveal } from '@/components/generals/reveal'
import { ArrowLink } from '@/components/generals/arrow-link'
import { SectionLabel } from '@/components/generals/section-label'
import { JustifiedSplit } from '@/components/studio/justified-split'
import { media } from '@/constants/media'
import { studioMeta } from '@/constants/site'

const SLOTS = [0, 1, 2, 3, 4]

const OPEN_SLOTS = 2

const SLOT_MONTH = "August '26"

const LEAD = "Tell us what you're building."

const TAIL = "We'll tell you how we'd make it land."

const PHONE_HREF = `tel:${studioMeta.phone.replace(/[^+\d]/g, '')}`

export const StudioContact: FC = (): ReactNode => {
    const scope = useRef<HTMLElement>(null)

    useGsap(() => {
        const root = scope.current

        if (!root || prefersReducedMotion()) {
            return
        }

        /* The two free slots breathe, the three taken ones do not. */
        const open = root.querySelectorAll<HTMLElement>('[data-slot="open"]')

        const pulse = gsap.to(open, {
            opacity: 0.35,
            duration: 1.15,
            repeat: -1,
            yoyo: true,
            stagger: 0.3,
            ease: 'sine.inOut'
        })

        const plate = root.querySelector<HTMLElement>('[data-contact-plate] img')

        if (plate) {
            gsap.fromTo(plate,
                { yPercent: -8, scale: 1.14 },
                {
                    yPercent: 8,
                    scale: 1.04,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: root,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: 0.8
                    }
                }
            )
        }

        return () => {
            pulse.kill()
        }
    }, scope, [])

    return (
        <section
            id='studio-contact'
            ref={scope}
            aria-labelledby='studio-contact-title'
            className='bloom relative z-10 overflow-hidden bg-ink text-paper'
        >
            <div className='relative mx-auto flex max-w-[1920px] flex-col gap-10 px-5 sec lg:gap-14 lg:px-[33px]'>
                <div className='hair-b grid grid-cols-1 items-start gap-8 pb-7 lg:grid-cols-4 lg:gap-5'>
                    <SectionLabel index='(10)' label='Book a call' tone='light' />

                    <Reveal from='up' distance={26} className='flex flex-col gap-4'>
                        <span className='flex items-center gap-1.5' aria-hidden='true'>
                            {SLOTS.map((slot) => (
                                <span
                                    key={slot}
                                    data-slot={slot < OPEN_SLOTS ? 'open' : 'taken'}
                                    className={cn(
                                        'block h-[6px] w-9 rounded-full bg-paper',
                                        slot < OPEN_SLOTS ? 'opacity-100' : 'opacity-40'
                                    )}
                                />
                            ))}
                        </span>

                        <p className='t-mono leading-[1.4]'>
                            <span>
                                {OPEN_SLOTS} slots for
                            </span>

                            {' '}

                            <span className='text-ash-soft'>
                                {SLOT_MONTH}
                            </span>
                        </p>
                    </Reveal>

                    <Reveal
                        from='up'
                        distance={26}
                        delay={0.12}
                        className='flex items-center gap-4 lg:col-span-2 lg:justify-end'
                    >
                        <img
                            src={media.people.founderAvatar}
                            alt=''
                            loading='lazy'
                            decoding='async'
                            className='size-[84px] rounded-[var(--radius-chip)] object-cover'
                        />

                        <span className='flex flex-col gap-2'>
                            <span className='t-cond text-[19px] leading-none text-paper'>
                                Jin Yuen
                            </span>

                            <span className='t-mono text-ash-soft'>
                                Founder
                            </span>
                        </span>
                    </Reveal>
                </div>

                {/* The texture used to run full bleed behind all of this, which
                    left a screen of grain with the content pushed out to the
                    four corners. Flat ink, two columns, and the photograph
                    contained as a plate. */}
                <div className='grid gap-10 lg:grid-cols-2 lg:items-end lg:gap-12'>
                    <div className='flex flex-col gap-8'>
                        <JustifiedSplit
                            as='h2'
                            id='studio-contact-title'
                            justify={false}
                            runs={[
                                { text: "Let's make you" },
                                { text: 'impossible', className: 'text-brand' },
                                { text: 'to ignore.' }
                            ]}
                            stagger={0.018}
                            className='t-display max-w-[14ch] text-paper'
                            style={{ fontSize: 'clamp(40px, 5vw, 72px)' }}
                        />

                        <JustifiedSplit
                            as='p'
                            justify={false}
                            runs={[
                                { text: LEAD, className: 'text-ash-soft' },
                                { text: TAIL, className: 'text-paper' }
                            ]}
                            stagger={0.008}
                            className='t-cond max-w-[42ch]'
                            style={{
                                fontSize: 'clamp(17px, 1.5vw, 21px)',
                                lineHeight: 1.14
                            }}
                        />

                        <Reveal from='up' distance={24}>
                            <ArrowLink href='/contact' label='Start a project' tone='light' />
                        </Reveal>
                    </div>

                    <div className='flex flex-col gap-7'>
                        <div
                            data-contact-plate
                            className='relative aspect-[16/10] w-full overflow-hidden rounded-[var(--radius-card)] bg-ink-soft'
                        >
                            <img
                                src={media.editorial.floorWide}
                                alt='The Noma Studio floor, where the first call ends up'
                                loading='lazy'
                                decoding='async'
                                className='absolute inset-0 size-full object-cover opacity-85'
                            />
                        </div>

                        <Reveal
                            from='up'
                            distance={24}
                            delay={0.1}
                            className='hair-t flex flex-wrap items-end justify-between gap-6 pt-6'
                        >
                            <a
                                href={PHONE_HREF}
                                data-cursor='link'
                                className='t-cond nums text-paper transition-colors duration-500 hover:text-brand'
                                style={{ fontSize: 'clamp(23px, 2.2vw, 30px)' }}
                            >
                                {studioMeta.phone}
                            </a>

                            <a
                                href={`mailto:${studioMeta.email}`}
                                data-cursor='link'
                                className='t-mono text-ash-soft transition-colors duration-500 hover:text-brand'
                            >
                                {studioMeta.email}
                            </a>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    )
}
